import React, { createContext, useState, useCallback, useEffect } from 'react';
import { api } from '../services/api';
import { refreshTokenFn } from '../services/auth';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [redirectTo, setRedirectTo] = useState(null);

  // Vérifier le token au chargement
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await api.get('/user/profile/');
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
      } catch (err) {
        console.error("Erreur de vérification:", err);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        setError("Session expirée. Veuillez vous reconnecter.");
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      setLoading(true);
      setError(null);

      if (!email || !password) {
        throw new Error("L'email et le mot de passe sont requis");
      }

      const response = await api.post('/token/', { 
        email: email.trim(), 
        password: password.trim() 
      });

      const data = await response.json();

      // Stocker les tokens
      localStorage.setItem('accessToken', data.access);
      localStorage.setItem('refreshToken', data.refresh);

      // Récupérer les informations de l'utilisateur
      try {
        const userResponse = await api.get('/user/profile/');
        const userData = await userResponse.json();
        setUser(userData);
        setIsAuthenticated(true);
        
        // Déclencher l'événement pour exécuter les actions en attente
        if (pendingAction) {
          window.dispatchEvent(new CustomEvent('pendingActionReady', {
            detail: { pendingAction }
          }));
        }
      } catch (userError) {
        console.error("Erreur lors de la récupération du profil:", userError);
        // On ne bloque pas la connexion si la récupération du profil échoue
      }

      return data;
    } catch (err) {
      console.error("Erreur de connexion:", err);
      setError(err.message);
      setIsAuthenticated(false);
      setUser(null);
      throw err;
          } finally {
        setLoading(false);
      }
    }, [pendingAction]);

  const logout = useCallback(() => {
    try {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
      setPendingAction(null);
      setRedirectTo(null);
    } catch (err) {
      console.error("Erreur lors de la déconnexion:", err);
      setError("Erreur lors de la déconnexion");
    }
  }, []);

  const refreshToken = useCallback(async () => {
    const refresh = localStorage.getItem('refreshToken');
    if (!refresh) {
      setError("Session expirée");
      return false;
    }

    try {
      const newToken = await refreshTokenFn();

      if (newToken) {
        localStorage.setItem('accessToken', newToken);
        return true;
      } else {
        throw new Error('Session expirée');
      }
    } catch (err) {
      console.error('Erreur lors du rafraîchissement du token:', err);
      logout();
      setError("Session expirée. Veuillez vous reconnecter.");
      return false;
    }
  }, [logout]);

  const setPendingCartAction = useCallback((action) => {
    setPendingAction(action);
  }, []);

  const clearPendingAction = useCallback(() => {
    setPendingAction(null);
  }, []);

  const setRedirectPath = useCallback((path) => {
    setRedirectTo(path);
  }, []);

  const clearRedirectPath = useCallback(() => {
    setRedirectTo(null);
  }, []);

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      user,
      loading,
      error,
      login,
      logout,
      refreshToken,
      pendingAction,
      setPendingCartAction,
      clearPendingAction,
      redirectTo,
      setRedirectPath,
      clearRedirectPath
    }}>
      {children}
    </AuthContext.Provider>
  );
}; 